import { useMemo } from 'react';
import { PerformanceSummary } from './usePartnerAnalytics';
import { DateRange } from 'react-day-picker';

export interface AnalyticsFilters {
  dateRange?: DateRange;
  prequalStatus?: string;
  ratingRange?: [number, number];
  tradeSpecialties?: string[];
  searchQuery?: string;
  documentStatus?: string[];
}

export function usePartnerAnalyticsFilters(
  data: PerformanceSummary[] | undefined,
  filters: AnalyticsFilters
) {
  return useMemo(() => {
    if (!data) return [];

    let filtered = [...data];

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query)
      );
    }

    // Prequalification status filter
    if (filters.prequalStatus && filters.prequalStatus !== 'all') {
      filtered = filtered.filter(item => item.prequalification_status === filters.prequalStatus);
    }

    // Rating range filter
    if (filters.ratingRange) {
      const [min, max] = filters.ratingRange;
      filtered = filtered.filter(item => {
        const rating = item.overall_rating || 0;
        return rating >= min && rating <= max;
      });
    }

    // Date range filter (based on last activity)
    if (filters.dateRange?.from) {
      filtered = filtered.filter(item => {
        const lastDate = item.last_rating_date || item.last_document_upload;
        if (!lastDate) return false;
        const itemDate = new Date(lastDate);
        const fromDate = filters.dateRange!.from!;
        const toDate = filters.dateRange!.to || new Date();
        return itemDate >= fromDate && itemDate <= toDate;
      });
    }

    // Document status filter
    if (filters.documentStatus && filters.documentStatus.length > 0) {
      filtered = filtered.filter(item => {
        return filters.documentStatus!.some(status => {
          if (status === 'verified' && item.verified_documents > 0) return true;
          if (status === 'pending' && item.pending_documents > 0) return true;
          if (status === 'rejected' && item.rejected_documents > 0) return true;
          return false;
        });
      });
    }

    return filtered;
  }, [data, filters]);
}
